import global from "../global.js";
import {json_apply_modifications} from "@/prototype/utils/JSON.js";
import StateMachine from "statemachine";
import UsersDebug from "./UsersDebug.js";
import AndroidApp from "./AndroidApp.js";
import {get_translation} from "svg-utils";

const d3 = global.d3;

const log = global.log.getLogger("f_mtac.users.theUser");
log.setLevel(log.levels.WARN);

const CAP_GEO = 1 << 0,
    CAP_GEO_POS = 1 << 1;

const MIN_DISTANCE_METERS = 10;
 
/**
 * This module handles mainly the geolocation of the user (the device) that runs Tacteam client
 * It switches from JS geolocation (see Geolocation.js) to Android geolocation to no geolocation, 
 * depending on if the device is linked to a resource and depending on if the room is shared
 * It subscribes to several events (space.share, object.delete, etc...) and adjust geolocation 
 * state machine.
 * Note "TheUser" could have been named "TheDevice"
 */
const TheUser = function () {
    var space = null;

    // android_service_started: true if the android geoloc service has been started with success
    var android_service_started = false;

    // space_shared = true if sharing ongoing
    var space_shared = false;

    this.debug = new UsersDebug(this);
    let androidApp = new AndroidApp();

    /**
     * @brief callback on object creation. If the object is linked to the user, start geoloc
     * @param {*} param0, description of the object
     * @returns nothin
     */
    const object_on_creating = ({object_data}) => {
        log.debug("TheUser.object_on_creating ", object_data);
        /*if (global.geolocation.required) {
            return;
        }*/
        if (object_data.attributes?.dataType !== "resource") {
            return;
        }
        if (object_data.attributes?.dataUser !== global.server.registrar.user.login) {
            return;
        }
        log.debug(`TheUser.object_on_creating sm ${sm.state.name} -> enabled`);

        // TODO request f_geoloc to add {user,desktop} in f_geoloc database
        this.start_geoloc();
    };

    /**
     * @brief callback on object deletion. If the object was the last linked to the user, stop geoloc
     * @param {*} param0, description of the object
     * @returns nothin
     */
    const object_on_deleting = ({object_data}) => {
        log.debug("TheUser.object_on_deleting ", object_data);
        /*if (global.geolocation.required) {
            return;
        }*/
        if (object_data.attributes?.dataType !== "resource") {
            return;
        }
        if (object_data.attributes?.dataUser !== global.server.registrar.user.login) {
            return;
        }

        setTimeout(() => {
            on_user_unlinked(object_data);
        }, 200);
    };

    /**
     * @brief callback on object modification.
     *        If the object is linked to the user, start geoloc
     *        If the object is unlinked, stop geoloc
     * @param {*} object_data, description of the object before the modifications
     * @param {*} modifications, the modifications to apply
     * @returns nothin
     */
    const object_on_updating = async ({object_data}, modifications) => {
        log.debug("TheUser.object_on_updating in (callback on space modifications)", {object_data, modifications});
        /*if (global.geolocation.required) {
            log.debug("TheUser.object_on_updating ");
            return;
        }*/
        //log.debug("TheUser.object_on_updating dataUser before", object_data.attributes.dataUser);
        //log.debug("TheUser.object_on_updating hash", modifications.object_hash);
        let dataUser_before_update = object_data.attributes.dataUser;
        if (object_data.attributes?.dataType !== "resource") {
            log.debug("TheUser.object_on_updating not a resource");
            return;
        }

        let modified = json_apply_modifications(object_data, "object_data", modifications);
        if (!modified) {
            log.debug("TheUser.object_on_updating not modified");
            return;
        }
        log.debug("TheUser.object_on_updating dataUser after", object_data.attributes.dataUser);
        // check if the object is linked to the user
        if (object_data.attributes?.dataUser == global.server.registrar.user.login) {
            log.debug(`TheUser.object_on_debuging sm ${sm.state.name} -> enabled`);
            this.start_geoloc();
            return;
        }

        // check if the object has been unlinked from the user
        if (object_data.attributes?.dataUser == undefined && dataUser_before_update == global.server.registrar.user.login) {
            log.debug("TheUser.object_on_updating wait a bit and call on_user_unlinked()");

            // Uggly but the only way found to avoid
            // exception Dexie TransactionInactiveError
            setTimeout(() => {
                on_user_unlinked(object_data);
            }, 200);
        }
        log.debug("TheUser.object_on_updating out");
    };

    /**
     * @brief actions to do when the user is unlinked. Check if the user is linked to other objects, if not stop geoloc
     * @param {*} object_data description of the user
     */
    const on_user_unlinked = async (object_data) => {
        log.debug("TheUser.on_user_unlinked the user is unlinked");
        // check if the user is present elsewhere in the space
        let list_objects = await space.find_objects_linked_to_user(global.server.registrar.user.login);
        if (list_objects.length == 0) {
            log.debug("TheUser.on_user_unlinked user not found elsewhere");
            sm.transition.disabled({});
        } else if (list_objects.length == 1) {
            // check the user
            // because the call to update the db is not synchron,
            // the user is probably still linked the DB at this point
            log.debug(`TheUser.on_user_unlinked ${list_objects[0].object_data.attributes.transform} === ${object_data.attributes.transform}`);
            if (list_objects[0].object_data.attributes.transform === object_data.attributes.transform) {
                log.debug("TheUser.on_user_unlinked user beign removed found");
                sm.transition.disabled({});
            }
        }
    };

    // update the local user in the users list
    const update_me_in_users = () => {
        let {login, name, capabilities} = global.server.registrar.user;
        log.debug(`TheUser.self_update login = ${login}`);
        if (login === undefined) {
            return;
        }
        global.users.user_update_name(login, name, capabilities);
    };

    const update_capabilities = (geolocation_state) => {
        switch (geolocation_state) {
            case "enabled":
                global.server.registrar.set_capability(CAP_GEO | CAP_GEO_POS);
                break;
            case "errored":
                global.server.registrar.unset_capability(CAP_GEO_POS);
                break;
            case "disabled":
                global.server.registrar.unset_capability(CAP_GEO | CAP_GEO_POS);
                break;
        }
    };

    /**
     * @brief callback on geolocation state change
     */
    global.event_bus.subscribe("geolocation.state", ({state}) => {
        log.debug("TheUser -> on geolocation.state");
        update_capabilities(state);
        update_me_in_users();
    });

    /**
     * @brief callback on changement of space.
     *      Subscribe to objects notifications in the new space
     *      If there are  objects linked to the user in the new space, then start geoloc
     * @param old_space
     * @param new_space
     */
    global.event_bus.subscribe("storage.space.changed", async ({old_space, new_space}) => {
        log.debug("TheUser -> on storage.space.changed", {old_space, new_space});

        // disable current geolocation if any
        await sm.transition.disabled({});

        if (old_space !== null) {
            // remove hooks on space.object modifications
            old_space.off("objects", "creating", object_on_creating);
            old_space.off("objects", "updating", object_on_updating);
            old_space.off("objects", "deleting", object_on_deleting);
        }
        space = null;
        if (new_space !== null) {
            space = new_space;
            // add hooks on space.object modifications
            new_space.on("objects", "creating", object_on_creating);
            new_space.on("objects", "updating", object_on_updating);
            new_space.on("objects", "deleting", object_on_deleting);

            // find if user is linked and start geoloc
            let list_objects = await space.find_objects_linked_to_user(global.server.registrar.user.login);
            if (list_objects.length > 0) {
                this.start_geoloc();
            }
        }
    });

    /**
     * @brief call back on sharing the room. Check if the Android service is running to use it if necessary
     * change from JS mechanism to Android mechanism
     */
    global.event_bus.subscribe("storage.shared", async () => {
        log.debug("TheUser -> on storage.shared in", sm.state);
        space_shared = true;
        // check android service at each sharing
        android_service_started = await androidApp.check_service();
        if (sm.state.name === "enabled_js" && android_service_started === true) {
            // stop JS geolocation when sharing and if android app is started
            sm.transition.disabled({});
        }

        // if there are still resources with the user
        let list_objects = await space.find_objects_linked_to_user(global.server.registrar.user.login);
        if (list_objects.length > 0) {
            this.start_geoloc();
        }
    });

    /**
     * @brief call back on stopping sharing the room.
     * change from Anroid mechanism to JS mechanism
     */
    global.event_bus.subscribe("storage.unshared", async () => {
        log.debug("TheUser -> on storage.unshared", sm.state);
        space_shared = false;
        if (sm.state.name == "enabled_android") {
            sm.transition.disabled({});
        }
        // if there are still resources with the user
        let list_objects = await space.find_objects_linked_to_user(global.server.registrar.user.login);
        if (list_objects.length > 0) {
            this.start_geoloc();
        }
    });

    global.event_bus.subscribe("storage.context.deleted", async ({desktop_index}) => {
        log.debug("TheUser -> on context.deleted:", desktop_index);
        if (sm.state.name !== "disabled") {
            let list_objects = await space.find_objects_linked_to_user(global.server.registrar.user.login);
            if (list_objects.length == 0) {
                sm.transition.disabled({});
            }
        }
    });

    /*
     * @brief function used to display far resources near the background image
     */
    const position_boundaries = async (context, [x, y]) => {
        let map_media_id = await context.map_media_id;
        if (map_media_id === undefined) {
            return [x, y];
        }
        let map_media = await global.medias.load_media({local_id: map_media_id});
        if (map_media === undefined) {
            return [x, y];
        }
        let {width: w, height: h} = map_media;
        // padding
        const px = w / 10;
        const py = h / 10;
        x = Math.min(Math.max(-px, x), w + px);
        y = Math.min(Math.max(-py, y), h + py);
        return [x, y];
    };

    const get_object_translate = (transform) => {
        let {x, y} = get_translation(transform);
        return [x + 100, y + 100];
    };

    const set_object_translate = ([x, y]) => {
        return `translate(${x - 100},${y - 100})`;
    };

    /**
     * @brief For each desktop of the space, modify the resource of the user in the Dexie database (via object_update_path)
     * @param {*} position
     * @param {*} refresh
     * @returns
     */
    const update_positions_in_database = async (position, refresh) => {
        log.debug("TheUser.update_positions_in_database in ", {position, refresh, space});
        if (space === null) {
            return;
        }
        for (let context of space.context) {
            if (context === null) {
                continue;
            }
            if (context.projection.project === null) {
                continue;
            }
            log.debug("TheUser.update_positions_in_database context ", context);
            let {longitude: E1, latitude: N1} = position.coords;
            log.debug(`TheUser.update_positions_in_database longitude = ${E1}, latitude = ${N1}`);
            let [X1, Y1] = context.projection.project([E1, N1]);
            log.debug(`TheUser.update_positions_in_database X1 = ${X1}, Y1 = ${Y1}`);
            this.debug.trace_update(0, X1, Y1);
            // Kalman filter
            let state = context.projection.filter.feed({observation: [X1, Y1], time: performance.now()}); // position.timestamp
            if (state !== null) {
                [X1, Y1] = state.mean;
            }

            // do not use position_boundaries in google map mode
            if (!global.scene.map.is_gis) {
                [X1, Y1] = await position_boundaries(context, [X1, Y1]);
            }
            [E1, N1] = context.projection.project_invert([X1, Y1]);
            this.debug.trace_update(1, X1, Y1);

            // before associating device to user => coordinates are calculated from resource position on the map
            // when associating device to user => if position  change < MIN_DISTANCE_METERS, the user position won't be updated
            // and timestamp neither
            // => force timestamp update just after associating device to user (so after refresh)
            // time has to be updated before transform, because is nothing to be updated for transform => time not updated !?!
            log.error("TheUser.update_positions_in_database TODO find a way to update dataLastGeolocTime");
            if (refresh) {
                log.debug("TheUser.update_positions_in_database update dataUser and dataLastGeolocTime");
                // object_update_path(key, value, time)
                await context.object_update_path(
                    {
                        "object_data.attributes.dataUser": global.server.registrar.user.login
                    },
                    {
                        "object_data.attributes.dataLastGeolocTime": position.timestamp * 1000
                    }
                );
            }
            let position_to_update = false;

            log.debug("TheUser.update_positions_in_database update dataUser and transform");
            // object_update_path(key, value, time)
            await context.object_update_path(
                {
                    "object_data.attributes.dataUser": global.server.registrar.user.login
                },
                {
                    "object_data.attributes.transform": (old_transform) => {
                        if (old_transform === undefined) {
                            return old_transform;
                        }
                        let [X0, Y0] = get_object_translate(old_transform);
                        let [E0, N0] = context.projection.project_invert([X0, Y0]);
                        if (d3.geoDistanceMeters([E0, N0], [E1, N1]) < MIN_DISTANCE_METERS) {
                            log.debug("object_update_path distance < MIN_DISTANCE_METERS");
                            return old_transform;
                        }
                        position_to_update = true;
                        return set_object_translate([X1, Y1]);
                    }
                },
                {
                    "object_data.attributes.dataLastGeolocTime": (old_dataLastGeoloc) => {
                        if (old_dataLastGeoloc === undefined) {
                            return old_dataLastGeoloc;
                        }
                        // AGPOC-6677 : add time of last position in resource attributes so that it can be displayed in resource information
                        // respect above algorithm : update object only if position has changed (distance > MIN_DISTANCE_METERS)
                        if (!position_to_update) {
                            log.debug("object_update_path distance < MIN_DISTANCE_METERS");
                            return old_dataLastGeoloc;
                        }
                        log.debug("TheUser.update_positions_in_database position to update");
                        return position.timestamp * 1000;
                    }
                }
            );
        } // end for

        log.debug("TheUser.update_positions_in_database out");
    };

    /**
     * @brief Start geolocation handled by Geolocation.js module
     */
    const start_js_geolocation = async () => {
        log.debug("TheUser.start_js_geolocation in", global.storage.selected_space.room);

        // no share on going, use JS mechanism to get position
        global.geolocation.on_position_change(update_positions_in_database);
    };

    /**
     * @brief Stop geolocation handled by Geolocation.js module
     */
    const stop_js_geolocation = async () => {
        log.debug("TheUser.stop_updating_position in");
        // no share on going, use JS mechanism to get position
        global.geolocation.off_position_change(update_positions_in_database);
    };

    /**
     * state machine of the user geolocation.
     * Geolocation is started as soon as the user is linked to a resource,
     * and is stopped when no more resources are linked to the user.
     * The states are:
     * - disabled: the device does not provide its geolocation
     * - enabled_js: the device provides its geolocation via the JS mechanism (see in Geolocation.js)
     * - enabled_android: the device provides its geolocation via the Android service
     */
    var sm = new StateMachine({
        name: "device_geolocation",
        shared_data: {},
        default_state: "disabled",
        states: {
            disabled: {
                in: () => {},
                out: () => {},
                transit: {
                    enable_android: {
                        state: "enabled_android",
                        handler: () => {
                            log.debug("TheUser.sm.disabled.transit.enable_android");
                            return true;
                        }
                    },
                    enable_js: {
                        state: "enabled_js",
                        handler: () => {
                            log.debug("TheUser.sm.disabled.transit.enable_js");
                            return true;
                        }
                    }
                },
                schedule: {}
            },
            // enable in a sharing room, use ANDROID service
            enabled_android: {
                in: async () => {
                    // actions to do when entering state "enabled"
                    log.debug("TheUser.sm.enabled_android in");
                    let started = await androidApp.start_geolocation();
                    if (started === false) {
                        sm.transition.enabled_js({});
                    }
                },
                out: () => {
                    // actions to do when leaving state "enabled"
                    log.debug("TheUser.sm.enabled_android out");
                    if (androidApp.geolocation_started === true) {
                        androidApp.stop_geolocation();
                    }
                },
                transit: {
                    disable: {
                        state: "disabled",
                        handler: () => {
                            log.debug("TheUser.sm.enabled_android.transit.disable");
                            return true;
                        }
                    },
                    enable_js: {
                        state: "enabled_js",
                        handler: () => {
                            log.debug("TheUser.sm.enabled_android.transit.enable_js");
                            return true;
                        }
                    }
                },
                schedule: {}
            },
            // Enable without sharing, use JS mechanism
            enabled_js: {
                in: async () => {
                    // actions to do when entering state "enabled"
                    log.debug("TheUser.sm.enabled_js in");
                    start_js_geolocation();
                    //nb_enabled_requested ++;
                },
                out: () => {
                    // actions to do when leaving state "enabled"
                    log.debug("TheUser.sm.enabled_js out");
                    stop_js_geolocation();
                },
                transit: {
                    disable: {
                        state: "disabled",
                        handler: () => {
                            log.debug("TheUser.sm.enabled_js.transit.disable");
                            return true;
                        }
                    },
                    enable_android: {
                        state: "enabled_android",
                        handler: () => {
                            log.debug("TheUser.sm.enabled_js.transit.enable_android");
                            return true;
                        }
                    }
                },
                schedule: {}
            }
        }
    });

    /*
     * Get geolocation from JS mechanism and update database
     **/
    /*this.refresh_position = async () => {
        log.debug("TheUser.refresh_position in");
        var position = await global.geolocation.current_position();
        log.debug("TheUser.refresh_position ", position);
        if (position === null) {
            return;
        }
        
        //update_positions_in_database(position, true);
    };*/

    this.start_geoloc = () => {
        log.debug(`TheUser.start_geoloc in space_shared=${space_shared}, android_service_started=${android_service_started}`);

        if (space_shared === true && android_service_started === true) {
            sm.transition.enabled_android({});
        } else {
            sm.transition.enabled_js({});
        }
        log.debug("TheUser.start_geoloc out");
    };

    this.init = () => {
        log.debug("TheUser.init in");
        update_capabilities(global.geolocation.state);
        update_me_in_users();
    };
};


export default TheUser;